import { User } from '@supabase/supabase-js';
import {
  TajirAgentDefinition,
  TajirAgentKey,
  TajirPlan,
  TajirPlanPolicy,
  TajirUsageSummary,
} from '../types/tajir';

// ------------------------------------------------------------
// Tajir agent catalog — the real "compute tier" dimension.
// Capabilities (web search, vision) are applied on top via toggles.
// ------------------------------------------------------------
export const TAJIR_AGENTS: Record<TajirAgentKey, TajirAgentDefinition> = {
  fast: {
    key: 'fast',
    provider: 'deepseek',
    label: 'Light',
    shortLabel: 'Light',
    tagline: 'Risposte rapide',
    description: 'Veloce e diretto: spiegazioni di metriche, dubbi rapidi, prima lettura.',
    modelFamily: 'DeepSeek',
    creditWeight: 1,
    supportsWebSearch: false,
    supportsVision: false,
    usesReasoning: false,
    order: 1,
  },
  thinking: {
    key: 'thinking',
    provider: 'moonshot',
    label: 'Normal',
    shortLabel: 'Normal',
    tagline: 'Equilibrio tra velocità e ragionamento',
    description: 'Più struttura e contesto: review, pattern, piani migliorativi graduali.',
    modelFamily: 'Kimi',
    creditWeight: 3,
    supportsWebSearch: true,
    supportsVision: true,
    usesReasoning: true,
    order: 2,
  },
  expert: {
    key: 'expert',
    provider: 'moonshot',
    label: 'Expert',
    shortLabel: 'Expert',
    tagline: 'Analisi approfondita',
    description: 'Confronto report, fragilità della strategia e lettura metrica-pattern-rischio.',
    modelFamily: 'Kimi',
    creditWeight: 6,
    supportsWebSearch: true,
    supportsVision: true,
    usesReasoning: true,
    order: 3,
  },
  max: {
    key: 'max',
    provider: 'anthropic',
    label: 'Advanced',
    shortLabel: 'Advanced',
    tagline: 'In arrivo',
    description: 'Tier futuro per analisi avanzate e memoria estesa.',
    modelFamily: 'Claude',
    creditWeight: 12,
    supportsWebSearch: false,
    supportsVision: false,
    usesReasoning: true,
    order: 4,
  },
};

// ------------------------------------------------------------
// Token economics
// ------------------------------------------------------------
/**
 * Blended API cost of one token, in USD. This mirrors the rate the edge
 * function already meters with (`totalTokens * 0.000002` in buildEstimatedCost),
 * i.e. $2 per 1M tokens. Plan allowances are simply the USD cap expressed in
 * this unit, so the two can never disagree.
 */
export const TOKEN_COST_USD = 0.000002;

/** Convert a USD budget into the token allowance shown to users. */
export const usdToTokens = (usd: number): number => Math.round(usd / TOKEN_COST_USD);

/**
 * Add-on token packs, sold on top of any plan (including Free).
 * Purchased tokens are a BALANCE: they do not reset with the monthly cycle and
 * are only consumed once the plan's monthly allowance is exhausted.
 */
export interface TokenPack {
  id: string;
  label: string;
  tokens: number;
  priceEur: number;
  /** Stripe Price id env var to wire at checkout time. */
  stripePriceEnv: string;
}

export const TOKEN_PACKS: TokenPack[] = [
  { id: 'tokens-1m',  label: '1M tokens',  tokens: 1_000_000,  priceEur: 4.90,  stripePriceEnv: 'STRIPE_PRICE_TOKENS_1M' },
  { id: 'tokens-3m',  label: '3M tokens',  tokens: 3_000_000,  priceEur: 12.90, stripePriceEnv: 'STRIPE_PRICE_TOKENS_3M' },
  { id: 'tokens-10m', label: '10M tokens', tokens: 10_000_000, priceEur: 39.90, stripePriceEnv: 'STRIPE_PRICE_TOKENS_10M' },
];

// ------------------------------------------------------------
// Plan policies — source of truth for gating (duplicated server-side
// in the edge function). Budget target: ~5 €/utente/mese on Base.
// ------------------------------------------------------------
export const TAJIR_PLAN_POLICIES: Record<TajirPlan, TajirPlanPolicy> = {
  free: {
    plan: 'free',
    label: 'Freemium',
    // BETA: Free is a taste of Tajir, not a usable daily allowance.
    dailyCredits: 3,
    monthlyMessageCredits: 25,
    monthlyWebSearches: 0,
    weeklyImages: 0,
    monthlyImages: 0,
    monthlyDeepThinking: 2,
    monthlyCostLimitUsd: 0.08,
    monthlyTokens: 40_000,
    maxAttachments: 0,
    memoryDays: 0,
    agents: ['fast'],
    canUseWebSearch: false,
    canUseVision: false,
    canUseDeepThinking: true,
  },
  light: {
    plan: 'light',
    label: 'Light',
    dailyCredits: 10,
    monthlyMessageCredits: 150,
    monthlyWebSearches: 0,
    weeklyImages: 3,
    monthlyImages: 12,
    monthlyDeepThinking: 20,
    monthlyCostLimitUsd: 1.65,   // ~€1.50/mese di costi API
    monthlyTokens: 825_000,
    maxAttachments: 1,
    memoryDays: 7,
    agents: ['fast', 'thinking'],
    canUseWebSearch: false,
    canUseVision: true,
    canUseDeepThinking: true,
  },
  base: {
    plan: 'base',
    label: 'Base',
    dailyCredits: 25,
    monthlyMessageCredits: 300,
    monthlyWebSearches: 60,
    weeklyImages: 10,
    monthlyImages: 30,
    monthlyDeepThinking: 60,
    monthlyCostLimitUsd: 4.40,   // ~€4/mese di costi API
    monthlyTokens: 2_200_000,
    maxAttachments: 4,
    memoryDays: 30,
    agents: ['fast', 'thinking', 'expert'],
    canUseWebSearch: true,
    canUseVision: true,
    canUseDeepThinking: true,
  },
  advanced: {
    plan: 'advanced',
    label: 'Advanced',
    dailyCredits: 125,
    monthlyMessageCredits: 1200,
    monthlyWebSearches: 240,
    weeklyImages: 40,
    monthlyImages: 150,
    monthlyDeepThinking: 240,
    monthlyCostLimitUsd: 22.00,  // 5× base (~€20/mese di costi API)
    monthlyTokens: 11_000_000,
    maxAttachments: 6,
    memoryDays: 90,
    agents: ['fast', 'thinking', 'expert', 'max'],
    canUseWebSearch: true,
    canUseVision: true,
    canUseDeepThinking: true,
  },
};

// ------------------------------------------------------------
// Timezone helpers (Europe/Rome) for daily/weekly/monthly windows
// ------------------------------------------------------------
export const getRomeDateString = (): string => {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Rome',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(new Date());
};

export const normalizeTajirPlan = (rawValue: unknown): TajirPlan => {
  const value = String(rawValue || '').toLowerCase().trim();

  if (['advanced', 'max', 'claude'].includes(value)) return 'advanced';
  if (['base'].includes(value)) return 'base';
  if (['light'].includes(value)) return 'light';
  if (['free', 'freemium', 'trial', 'starter', ''].includes(value)) return 'free';
  if (['pro', 'premium'].includes(value)) return 'base';

  return 'free';
};

/**
 * app_metadata only. The user_metadata fallbacks are gone: that field is
 * writable by the account owner, so reading it here made the UI show a plan the
 * server would refuse — and the same fallbacks in tajir-chat and diary-ai meant
 * the server did NOT refuse it. Kept aligned deliberately, so what the user is
 * shown and what they actually get cannot drift apart again.
 */
export const extractUserTajirPlan = (user: User | null): TajirPlan => {
  const metadataPlan =
    user?.app_metadata?.subscription_plan ||
    user?.app_metadata?.plan;

  return normalizeTajirPlan(metadataPlan);
};

export const getTajirPolicy = (plan: TajirPlan): TajirPlanPolicy => {
  const policy = TAJIR_PLAN_POLICIES[plan] || TAJIR_PLAN_POLICIES.free;

  if (process.env.REACT_APP_TAJIR_UNLOCK_ALL === 'true') {
    return {
      ...policy,
      // Dev mode: bump tutti i cap a valori che non si esauriscono mai.
      // Allineato all'edge function (supabase/functions/tajir-chat/index.ts).
      dailyCredits: 999999,
      monthlyMessageCredits: 999999,
      monthlyWebSearches: 999999,
      weeklyImages: 999999,
      monthlyImages: 999999,
      monthlyDeepThinking: 999999,
      monthlyCostLimitUsd: 999999,
      monthlyTokens: 999_999_999,
      maxAttachments: 99,
      memoryDays: 365,
      agents: ['fast', 'thinking', 'expert', 'max'],
      canUseWebSearch: true,
      canUseVision: true,
      canUseDeepThinking: true,
    };
  }

  return policy;
};

export const canUseTajirAgent = (plan: TajirPlan, agent: TajirAgentKey): boolean => {
  return getTajirPolicy(plan).agents.includes(agent);
};

export const canUseWebSearch = (plan: TajirPlan, agent: TajirAgentKey): boolean => {
  const policy = getTajirPolicy(plan);
  const def = TAJIR_AGENTS[agent];
  return policy.canUseWebSearch && def.supportsWebSearch;
};

export const canUseVision = (plan: TajirPlan, agent: TajirAgentKey): boolean => {
  const policy = getTajirPolicy(plan);
  const def = TAJIR_AGENTS[agent];
  return policy.canUseVision && def.supportsVision;
};

export const canUseDeepThinking = (plan: TajirPlan): boolean => {
  // Deep thinking is offered to every paid plan and as a "taste" on free.
  // Quota enforcement is server-side via monthlyDeepThinking.
  return getTajirPolicy(plan).canUseDeepThinking;
};

export const isAttachmentAllowed = (plan: TajirPlan): boolean => getTajirPolicy(plan).maxAttachments > 0;

export const getVisibleTajirAgents = (): TajirAgentDefinition[] => {
  return Object.values(TAJIR_AGENTS).sort((a, b) => a.order - b.order);
};

// ------------------------------------------------------------
// Usage summary builder — used when the RPC is unavailable
// (e.g. migration 050 not yet applied). The edge function will
// always recompute the true values server-side before sending.
// ------------------------------------------------------------
export const buildUsageSummary = (
  plan: TajirPlan,
  input: {
    dayCreditsUsed?: number;
    dayMessageCreditsUsed?: number;
    monthMessageCreditsUsed?: number;
    monthWebSearchesUsed?: number;
    weekImagesUsed?: number;
    monthImagesUsed?: number;
    monthDeepThinkingUsed?: number;
    dailyCostUsedUsd?: number;
    weeklyCostUsedUsd?: number;
    monthlyCostUsedUsd?: number;
  } = {},
): TajirUsageSummary => {
  const policy = getTajirPolicy(plan);
  const dayUsed = Math.max(0, input.dayCreditsUsed ?? 0);
  const monthMsg = Math.max(0, input.monthMessageCreditsUsed ?? 0);
  const monthWeb = Math.max(0, input.monthWebSearchesUsed ?? 0);
  const weekImg = Math.max(0, input.weekImagesUsed ?? 0);
  const monthImg = Math.max(0, input.monthImagesUsed ?? 0);
  const monthDeep = Math.max(0, input.monthDeepThinkingUsed ?? 0);
  const dayCost = Math.max(0, input.dailyCostUsedUsd ?? 0);
  const weekCost = Math.max(0, input.weeklyCostUsedUsd ?? 0);
  const monthCost = Math.max(0, input.monthlyCostUsedUsd ?? 0);

  return {
    plan,
    dailyCredits: policy.dailyCredits,
    creditsUsed: dayUsed,
    creditsRemaining: Math.max(0, policy.dailyCredits - dayUsed),
    monthlyMessageCredits: policy.monthlyMessageCredits,
    monthlyMessageCreditsUsed: monthMsg,
    monthlyMessageCreditsRemaining: Math.max(0, policy.monthlyMessageCredits - monthMsg),
    monthlyWebSearches: policy.monthlyWebSearches,
    monthlyWebSearchesUsed: monthWeb,
    monthlyWebSearchesRemaining: Math.max(0, policy.monthlyWebSearches - monthWeb),
    weeklyImages: policy.weeklyImages,
    weeklyImagesUsed: weekImg,
    weeklyImagesRemaining: Math.max(0, policy.weeklyImages - weekImg),
    monthlyImages: policy.monthlyImages,
    monthlyImagesUsed: monthImg,
    monthlyImagesRemaining: Math.max(0, policy.monthlyImages - monthImg),
    monthlyDeepThinking: policy.monthlyDeepThinking,
    monthlyDeepThinkingUsed: monthDeep,
    monthlyDeepThinkingRemaining: Math.max(0, policy.monthlyDeepThinking - monthDeep),
    usageDate: getRomeDateString(),
    monthlyCostLimitUsd: policy.monthlyCostLimitUsd,
    monthlyCostUsedUsd: monthCost,
    weeklyCostUsedUsd: weekCost,
    dailyCostUsedUsd: dayCost,
  };
};
